import * as React from 'react';
import { FC, useCallback, useMemo } from 'react';
import { Col, Row } from 'reactstrap';
import { ReportFieldDefinition, ReportFieldFlag } from '../../../../models';
import { ReportFieldType } from '../../../../models';
import ReportField, { ReportFieldEditAction } from '../ReportField';

interface ReportFormContentProps {
    id?: string,
    fields: ReportFieldDefinition[],
    value?: any,
    showEditActions?: boolean,
    readOnly?: boolean,
    errors?: {[field: string]: string},
    aiSuggestions?: Record<string, string>,
    onChange?: (field: string, value: any) => void,
    onAction?: (fieldIndex: number, action: ReportFieldEditAction) => void,
}

const isSameRow = (field: ReportFieldDefinition) => [
    ReportFieldType.Text,
    ReportFieldType.IntNumber,
    ReportFieldType.FloatNumber,
    ReportFieldType.Date,
].includes(field.type) && !field.flags?.includes(ReportFieldFlag.Block);

const getColProps = (field: ReportFieldDefinition) => isSameRow(field)
    ? { xs: 12, sm: 6, lg: 4 }
    : { xs: 12 };

interface ReportFieldItemProps {
    id: string,
    field: ReportFieldDefinition,
    index: number,
    value: any,
    readOnly?: boolean,
    showEditActions?: boolean,
    error?: string,
    aiSuggestion?: string,
    onChange?: (field: string, value: any) => void,
    onAction?: (fieldIndex: number, action: ReportFieldEditAction) => void,
}

const ReportFieldItem: FC<ReportFieldItemProps> = React.memo(({ id, field, index, value, readOnly, showEditActions, error, aiSuggestion, onChange, onAction }) => {
    const handleChange = useCallback((newValue: any) => onChange && onChange(field.label, newValue), [onChange, field.label]);
    const handleAction = useCallback((action: ReportFieldEditAction) => onAction && onAction(index, action), [onAction, index]);
    const handleAcceptAiSuggestion = useCallback((suggestedValue: string) => onChange && onChange(field.label, suggestedValue), [onChange, field.label]);

    return (
        <Col {...getColProps(field)}>
            <ReportField
                id={`${id}-field-${index}`}
                fieldDefinition={field}
                readOnly={readOnly}
                showEditActions={showEditActions}
                error={error}
                onAction={handleAction}
                value={value}
                onChange={handleChange}
                aiSuggestion={aiSuggestion}
                onAcceptAiSuggestion={handleAcceptAiSuggestion}
            />
        </Col>
    );
});

const ReportFormContent: FC<ReportFormContentProps> = ({
    id,
    fields,
    value,
    showEditActions,
    readOnly,
    errors = {},
    aiSuggestions,
    onChange,
    onAction,
}) => {
    return (
        <Row>
            {fields.map((field, index) => (
                <ReportFieldItem
                    key={`${field.label || ''}-${field.type || ''}`}
                    id={id ?? 'report-content'}
                    field={field}
                    index={index}
                    value={value[field.label]}
                    readOnly={readOnly}
                    showEditActions={showEditActions}
                    error={errors[field.label]}
                    aiSuggestion={aiSuggestions?.[field.label]}
                    onChange={onChange}
                    onAction={onAction}
                />
            ))}
        </Row>
    );
}

export default ReportFormContent;
